import { useState, useRef, useEffect } from 'react'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import Paper from '@mui/material/Paper'

import { apiClient } from '~/api'

import Papa from 'papaparse'

interface WalmartReturnsProps {
    env: string
}

interface ReturnResult {
    return_id: string
    success: boolean
    message?: string
    error?: string
}

interface ApiResponse {
    results: ReturnResult[]
}

interface TaskResponse {
    task_id: string
    status: string
}

interface TaskStatusResponse {
    task_id: string
    status: string
    ready: boolean
    result?: ApiResponse
    error?: string
}

export function WalmartReturns({ env }: WalmartReturnsProps) {
    const [processing, setProcessing] = useState(false)
    const [results, setResults] = useState<ReturnResult[]>([])
    const [error, setError] = useState<string | null>(null)
    const [taskId, setTaskId] = useState<string | null>(null)
    const [polling, setPolling] = useState(false)

    const fileInputRef = useRef<HTMLInputElement>(null)
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)

    const parseAndUpload = async (file: File) => {
        setProcessing(true)
        setError(null)
        setResults([])
        setTaskId(null)
        setPolling(true)

        try {
            const text = await file.text()
            const parsed = Papa.parse<{ return_id: string; sku: string; quantity: string }>(text.trim(), { 
                skipEmptyLines: true,
                header: true
            })
            
            if (parsed.errors.length) {
                console.error(parsed.errors)
                throw new Error('Failed to parse CSV')
            }

            // Validate required columns
            const data = parsed.data
            if (data.length === 0) {
                throw new Error('CSV file is empty')
            }

            const firstRow = data[0]
            if (!firstRow.return_id || !firstRow.sku || !firstRow.quantity) {
                throw new Error('CSV must contain columns: return_id, sku, quantity')
            }

            // Submit the task
            const response = await apiClient.post(`integrations/rainforest/${env}/returns/close/`, {
                json: data
            }).json() as TaskResponse

            setTaskId(response.task_id)
            
            // Start polling for task completion
            startPolling(response.task_id)
            
        } catch (err: any) {
            setError(err.message || 'Failed to process returns')
            console.error('Upload error:', err)
            setProcessing(false)
            setPolling(false)
        }
    }

    const checkTaskStatus = async (taskId: string): Promise<TaskStatusResponse> => {
        const response = await apiClient.get(`integrations/rainforest/${env}/returns/task/${taskId}/status/`).json() as TaskStatusResponse
        return response
    }

    const startPolling = (taskId: string) => {
        const pollInterval = 5000 // 5 seconds
        const maxPollingTime = 5 * 60 * 1000 // 5 minutes
        const startTime = Date.now()

        const poll = async () => {
            try {
                const status = await checkTaskStatus(taskId)
                
                if (status.ready) {
                    // Task is complete, stop polling
                    if (pollingIntervalRef.current) {
                        clearInterval(pollingIntervalRef.current)
                        pollingIntervalRef.current = null
                    }
                    
                    setPolling(false)
                    setProcessing(false)
                    
                    if (status.status === 'SUCCESS' && status.result) {
                        setResults(status.result.results || [])
                    } else if (status.status === 'FAILURE') {
                        setError(status.error || 'Task failed')
                    }
                } else if (Date.now() - startTime > maxPollingTime) {
                    // Maximum polling time reached
                    if (pollingIntervalRef.current) {
                        clearInterval(pollingIntervalRef.current)
                        pollingIntervalRef.current = null
                    }
                    
                    setPolling(false)
                    setProcessing(false)
                    setError('Processing timed out after 5 minutes. Please check back later.')
                }
            } catch (err: any) {
                console.error('Polling error:', err)
                // Continue polling on error, but maybe log it
            }
        }

        // Initial check
        poll()
        
        // Set up interval for subsequent checks
        pollingIntervalRef.current = setInterval(poll, pollInterval)
    }

    useEffect(() => {
        // Cleanup polling interval on component unmount
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current)
                pollingIntervalRef.current = null
            }
        }
    }, [])

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0]
        if (selectedFile) {
            if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
                setError('Please select a CSV file')
                return
            }
            setResults([])
            void parseAndUpload(selectedFile)
        }
    }

    const handleReset = () => {
        // Stop any active polling
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current)
            pollingIntervalRef.current = null
        }
        
        setResults([])
        setError(null)
        setTaskId(null)
        setPolling(false)
        setProcessing(false)
        
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }

    return (
        <Stack spacing={3}>
            <Paper sx={{ p: 3 }}>
                <Stack spacing={2} alignItems="flex-start">
                    <Typography variant="h6">Walmart Returns - Item Receipts</Typography>

                    {/* Instructions */}
                    <Alert severity="info" sx={{ width: '50%' }}>
                        <Typography variant="body2">
                            The system will create ItemReceipts for the specified ReturnAuthorizations.
                            CSV should contain 3 columns with headers: <strong>return_id, sku, quantity</strong><br />
                        </Typography>
                    </Alert>

                    {/* Error Display */}
                    {error && (
                        <Alert severity="error" sx={{ width: '100%' }}>
                            {error}
                        </Alert>
                    )}
                    
                    {/* Processing State */}
                    {processing && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3, width: '100%' }}>
                            <CircularProgress />
                            <Typography variant="body2" sx={{ mt: 2 }}>
                                {polling ? 'Processing your returns... This may take a few minutes.' : 'Starting processing...'}
                            </Typography>
                            {taskId && (
                                <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary' }}>
                                    Task ID: {taskId}
                                </Typography>
                            )}
                        </Box>
                    )}

                    {/* Initial State - File Upload */}
                    {!processing && results.length === 0 && (
                        <>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".csv"
                                onChange={handleFileChange}
                                style={{ display: 'none' }}
                                id="walmart-returns-file-input"
                            />
                            
                            <label htmlFor="walmart-returns-file-input">
                                <Button
                                    variant="outlined"
                                    component="span"
                                    sx={{ mt: 2 }}
                                >
                                    Upload CSV
                                </Button>
                            </label>
                        </>
                    )}

                    {results.length > 0 && (
                        <Stack spacing={1} alignItems="flex-start" sx={{ width: '100%' }}>
                            
                            {results.map((result, index) => (
                                <Alert 
                                    key={index} 
                                    severity={result.success ? 'success' : 'error'}
                                    sx={{ mb: 1, width: '100%' }}
                                >
                                    <Typography variant="body2">
                                        <strong>Return ID: {result.return_id}</strong>
                                        <br />
                                        {result.success ? 
                                            `✓ ${result.message || 'Processed successfully'}` : 
                                            `✗ ${result.error || 'Processing failed'}`
                                        }
                                    </Typography>
                                </Alert>
                            ))}
                            
                            <Typography variant="body2" sx={{ mt: 2 }}>
                                <strong>Summary:</strong> {results.filter(r => r.success).length} successful, 
                                {results.filter(r => !r.success).length} failed
                            </Typography>

                            <Button
                                variant="outlined"
                                onClick={handleReset}
                                sx={{ mt: 3 }}
                            >
                                Reset
                            </Button>
                        </Stack>
                    )}
                </Stack>
            </Paper>
        </Stack>
    )
}

export default WalmartReturns
